import { AnyAction } from "redux";
import { StoreError } from "../../errors";
import {
  IdResourceState,
  buildIdResourceActionTypes,
  buildIdResourceActions,
  buildIdResourceReducer,
  buildIdResourceSelectors,
  initialIdResourceState,
} from "../../util/id-resource";
import {
  ACTION_ACCOUNT_SAVING,
  ACTION_ACCOUNT_SAVED,
  ACTION_ACCOUNT_SAVE_ERROR,
  ACTION_ACCOUNT_SAVE_RESET,
} from "./save";

/** The user's own profile. Mirrors the `users/<email>` Firestore doc shape
 *  except keyed and exposed for the Account screen specifically — this slice
 *  is single-user-per-session and orthogonal to the `users` slice (which
 *  loads "the related users for event X"). */
export type AccountProfile = {
  id: string; // email
  email: string;
  displayName: string | null;
};

/**
 * AccountState keys profiles by user email under id-resource so the load /
 * loaded / errored flow gets the same staleness guard as every other slice.
 *
 * If the user's email changes mid-session (logout + login as a different
 * account), the previous profile remains in `itemsById` but loadingFor /
 * errorFor / requestToken are scoped to the new email — no stale loading
 * spinner can leak across users.
 */
export type AccountState = IdResourceState<AccountProfile> & {
  save: {
    saving: boolean;
    saved: AccountProfile | null;
    error: StoreError | null;
  };
};

export const ACCOUNT_ACTION_TYPES = buildIdResourceActionTypes("account");
export const accountActions =
  buildIdResourceActions<AccountProfile>(ACCOUNT_ACTION_TYPES);
export const accountSelectors = buildIdResourceSelectors<
  { account: AccountState },
  AccountProfile
>((root) => root.account);

const idResourceReducer = buildIdResourceReducer<AccountProfile, AccountState>(
  ACCOUNT_ACTION_TYPES
);

const initialState: AccountState = {
  ...initialIdResourceState<AccountProfile>(),
  save: { saving: false, saved: null, error: null },
};

const commitProfile = (
  state: AccountState,
  profile: AccountProfile
): AccountState => ({
  ...state,
  itemsById: { ...state.itemsById, [profile.id]: profile },
});

export default function accountReducer(
  state: AccountState = initialState,
  action: AnyAction
): AccountState {
  // Run id-resource cases first (per-id load / loaded / errored / removed).
  let next = idResourceReducer(state, action);

  switch (action.type) {
    case ACTION_ACCOUNT_SAVING: {
      return {
        ...next,
        save: { ...next.save, saving: action.payload as boolean },
      };
    }
    case ACTION_ACCOUNT_SAVED: {
      const profile = action.payload as AccountProfile;
      const withCommit = commitProfile(next, profile);
      return {
        ...withCommit,
        save: { ...withCommit.save, saved: profile, error: null },
      };
    }
    case ACTION_ACCOUNT_SAVE_ERROR: {
      return {
        ...next,
        save: { ...next.save, error: action.payload as StoreError },
      };
    }
    case ACTION_ACCOUNT_SAVE_RESET: {
      return {
        ...next,
        save: { saving: false, saved: null, error: null },
      };
    }
    default:
      return next;
  }
}
